// frontend/src/features/livestock/photoUploadQueue.ts
/**
 * Upload queue for the photo inbox (docs/plans/2026-10-13-photo-inbox.md, 4.1).
 *
 * A module-level store, so a batch keeps going while the user moves around the
 * app; UploadProgressStrip shows it from ProtectedLayout. Batches run one after
 * another. For each batch:
 *
 * 1. Hash every file, one at a time, and skip a photo picked twice.
 * 2. Ask /check (200 hashes per request) which photos the tank already has.
 *    A batch of one photo skips this, so a duplicate comes back from the
 *    upload with its id and the Livestock page can open it.
 * 3. Prepare (shrink if needed) and upload: two uploads at a time, with
 *    preparation at most two photos ahead of them.
 *
 * Failures: a 409 duplicate counts as done (as uploaded when it follows this
 * photo's own failed attempt, after waiting while the server still processes
 * it), a full inbox stops the batch, a 429 or a 503 busy holds the whole queue
 * until Retry-After, and network errors and 5xx are retried twice (after 2 s
 * and 6 s) before the photo is marked failed with a Retry.
 *
 * While photos remain, the queue holds a screen wake lock (except while it
 * waits more than a minute on a 429 or busy answer), warns before the page is left, and on touch devices pauses while the page is hidden (iOS
 * suspends hidden pages; on a computer a background tab keeps going). It keeps
 * a count of photos not uploaded in localStorage so the next page load can say
 * so, refreshes the tank's inbox count as photos arrive, and forgets
 * everything on sign-out (clear).
 *
 * A batch with `assignTo: 'tank'` (Add Photos on the Tank Shots page; tank
 * shots design 6.5) adds every photo that reaches the inbox as a tank shot
 * (target 'tank', no crop): after its upload, a 409 duplicate, or /check's
 * in_inbox_items. The assign takes the same slot as the upload, is retried on
 * network errors, 5xx (502 "the assign didn't finish" included) and 409
 * in_progress (assigning again is idempotent), and is held by 429 and busy like
 * uploads; a photo still being processed is tried again until it is pending
 * (ASSIGN_PROCESSING_WAITS_MS). A photo isn't settled until it is `assigned`,
 * or `left_in_inbox` with a reason (or already on an organism: a photo is in
 * one place). Such a batch refreshes Tank Shots as photos land instead of the
 * inbox count (the inbox only at the end, when photos were left there), and its
 * photos waiting for their assign are kept in a second marker, so a reload
 * says they wait in Photos to sort.
 */

import { useSyncExternalStore } from 'react';
import {
  DEFAULT_BUSY_WAIT_S,
  photoInboxApi,
  PhotoInboxBusyError,
  PhotoInboxConflictError,
  PhotoInboxError,
  PhotoInboxNetworkError,
  PhotoRejectedError,
  PhotoTooLargeError,
  PhotoUploadCancelledError,
} from '@/api/photoInbox';
import { RateLimitError } from '@/api/rateLimit';
import { refreshInboxAfterUploads, refreshTankShots } from '@/hooks/photoInboxCache';
import { photoInboxKeys } from '@/hooks/photoInboxKeys';
import { queryClient } from '@/lib/queryPersistence';
import {
  PHOTO_INBOX_CHECK_MAX,
  type AssignTarget,
  type PhotoInboxAssignResult,
  type PhotoInboxCheckResponse,
  type PhotoInboxItem,
  type PhotoInboxStatus,
  type PhotoInboxUpload,
} from '@/types/photoInbox';
import {
  computeContentHash,
  PhotoPrepareError,
  prepareForUpload,
  type PreparedPhoto,
} from './photoMetadata';

/** Uploads in flight at once. */
export const UPLOAD_CONCURRENCY = 2;
/**
 * Photos prepared (or being prepared) ahead of the uploads. A prepared photo
 * holds its file to send (the original, or the shrunk JPEG) until it is
 * uploaded; decoding is one photo at a time and freed before the next.
 */
export const PREPARE_AHEAD = 2;
/** Waits before the retries of a network error or 5xx. */
export const RETRY_DELAYS_MS = [2000, 6000] as const;
/** Pause after a 429 without a usable Retry-After. */
export const DEFAULT_RATE_LIMIT_WAIT_S = 60;
/** A hold longer than this lets the screen sleep (the wake lock is released until it ends). */
export const WAKE_LOCK_MAX_HOLD_MS = 60_000;
/** Busy answers one photo may get before it is marked failed. */
export const MAX_BUSY_RETRIES = 10;
/**
 * Waits before sending a photo again when the server says it already has it,
 * still 'uploading', after this photo's own attempt failed: that attempt may be
 * the one being processed, and if processing fails the server lets it be sent again.
 */
export const OWN_DUPLICATE_WAITS_MS = [10_000, 20_000, 40_000] as const;
/** Upload progress is republished at most this often (about 4 times a second). */
export const PROGRESS_INTERVAL_MS = 250;
/** While a batch uploads, the inbox count is refreshed after this many uploads... */
export const COUNT_REFRESH_UPLOADS = 10;
/** ...or after an upload this long after the last refresh. */
export const COUNT_REFRESH_MS = 15_000;
/** localStorage key holding the number of photos not yet uploaded. */
export const UNFINISHED_MARKER_KEY = 'rdh-photo-uploads-unfinished';
/**
 * localStorage key holding, per tank, the number of a tank batch's photos
 * uploaded but not added to Tank Shots: JSON `{"<tankId>": count}`.
 */
export const UNSORTED_MARKER_KEY = 'rdh-photo-uploads-unsorted';
/** Waits before the retries of an assign's network error or 5xx (assigning again is idempotent). */
export const ASSIGN_RETRY_DELAYS_MS = [2000, 6000, 15_000] as const;
/** Waits before an assign is sent again after 409 in_progress (another assign of it is running). */
export const ASSIGN_IN_PROGRESS_WAITS_MS = [2000, 4000, 8000, 16_000] as const;
/** Waits before assigning a photo the server is still processing ('uploading'), until it is pending. */
export const ASSIGN_PROCESSING_WAITS_MS = [5000, 10_000, 20_000, 40_000] as const;

/** Where a batch's photos go: the inbox (default), or straight to Tank Shots. */
export type UploadAssignTo = 'tank';

export type UploadFailureReason = 'unsupported' | 'too_large' | 'network' | 'server' | 'unreadable';

/** What happened to one picked photo. */
export type UploadOutcome =
  /**
   * `processing` when the server still had it as 'uploading' after the waits
   * (an attempt of this photo that lost its connection, still being processed).
   */
  | { kind: 'uploaded'; id: number; processing?: true }
  /**
   * From /check, id and status come from its in_inbox_items, and are null
   * from a server older than tank shots (which returns neither); from a 409
   * duplicate, status is the existing row's. Only a 'pending' photo can be
   * opened in the assign view.
   */
  | { kind: 'in_inbox'; id: number | null; status: PhotoInboxStatus | null }
  /** Sorted already: on an organism, or a tank shot (target 'tank'). */
  | { kind: 'on_livestock'; livestockName: string; target?: AssignTarget }
  /** A tank batch's photo, added to Tank Shots; id is the inbox photo's. */
  | { kind: 'assigned'; id: number }
  /**
   * A tank batch's photo that stays in the inbox, to sort by hand, and why.
   * id is null when /check found it without one (a server older than tank
   * shots); retryable when the assign may still work (Retry assigns again).
   */
  | { kind: 'left_in_inbox'; id: number | null; reason: string; retryable?: true }
  | { kind: 'failed'; reason: UploadFailureReason; message: string }
  /** Picked twice in one batch, or left when the batch was stopped. */
  | { kind: 'skipped'; why: 'duplicate' | 'stopped' };

export type UploadFileStage =
  | 'waiting'
  | 'hashing'
  | 'preparing'
  | 'uploading'
  | 'retrying'
  | 'assigning'
  | 'done';
export type UploadBatchStatus = 'waiting' | 'running' | 'finished' | 'stopped';
export type UploadBatchStage = 'hashing' | 'checking' | 'uploading';

export interface UploadFileView {
  id: string;
  name: string;
  stage: UploadFileStage;
  outcome: UploadOutcome | null;
}

export interface UploadCounts {
  uploaded: number;
  inInbox: number;
  onLivestock: number;
  failed: number;
  skipped: number;
  /** Tank batches: added to Tank Shots, and left in the inbox to sort. */
  assigned: number;
  leftInInbox: number;
}

export interface UploadBatchView {
  id: string;
  tankId: string;
  tankName: string;
  /** 'tank' for a batch added straight to Tank Shots; missing for the inbox. */
  assignTo?: UploadAssignTo;
  status: UploadBatchStatus;
  stage: UploadBatchStage | null;
  /** Photos picked. */
  total: number;
  /** Photos hashed (or that couldn't be read), for progress while checking. */
  hashed: number;
  /** Photos with an outcome. */
  settled: number;
  counts: UploadCounts;
  /** 0 to 1, counting the part of in-flight uploads already sent. */
  progress: number;
  files: UploadFileView[];
  /** Why the batch was stopped (the server's message for a full inbox). */
  stopMessage: string | null;
}

/**
 * Why nothing is being sent: a 429 (rate_limited) or 503 busy holds the whole
 * queue until resumeAt; on a touch device, a hidden page.
 */
export type UploadPause =
  | { kind: 'rate_limited' | 'busy'; resumeAt: number }
  | { kind: 'hidden' }
  | null;

export interface InterruptedToSort {
  tankId: string | null;
  count: number;
}

export interface UploadQueueSnapshot {
  batches: UploadBatchView[];
  pause: UploadPause;
  /** Photos an earlier page load left unfinished (from the localStorage marker); 0 when none. */
  interrupted: number;
  /**
   * Photos of tank batches an earlier page load uploaded but didn't add to
   * Tank Shots, per tank: they wait in its Photos to sort. tankId is null for
   * a marker written before the tank was stored. Empty when none.
   */
  interruptedToSort: InterruptedToSort[];
}

export interface BatchResult {
  batchId: string;
  files: { name: string; outcome: UploadOutcome }[];
}

export interface EnqueuedBatch {
  batchId: string;
  /** Resolves when the batch first finishes or is stopped. */
  done: Promise<BatchResult>;
}

export interface EnqueueRequest {
  tankId: string;
  tankName: string;
  files: File[];
  /** 'tank': add each photo to Tank Shots once it is in the inbox. */
  assignTo?: UploadAssignTo;
}

export interface PhotoUploadQueue {
  subscribe(listener: () => void): () => void;
  getSnapshot(): UploadQueueSnapshot;
  /** Adds a batch for the tank; null when no files were picked. */
  enqueue(request: EnqueueRequest): EnqueuedBatch | null;
  /** Runs one failed photo again (network, server or read failures only). */
  retry(batchId: string, fileId: string): void;
  /** Runs every failed photo of the batch that can be retried. */
  retryFailed(batchId: string): void;
  /** Removes a finished or stopped batch from the strip. */
  dismiss(batchId: string): void;
  /** Reads the marker an earlier page load left, once, and clears it. */
  restoreInterrupted(): void;
  /** Dismisses the "didn't upload" message. */
  dismissInterrupted(): void;
  /** Dismisses the "waiting in Photos to sort" message of one tank (null: the one without a tank). */
  dismissInterruptedToSort(tankId: string | null): void;
  /**
   * Forgets everything, for sign-out: stops the work and removes every batch,
   * the marker and the interrupted message. Uploads in flight are aborted;
   * answers to other requests already sent are ignored.
   */
  clear(): void;
}

/** The I/O the queue does, injectable for tests. */
export interface UploadQueueDeps {
  hash(file: File): Promise<string>;
  check(tankId: string, hashes: string[]): Promise<PhotoInboxCheckResponse>;
  prepare(file: File): Promise<PreparedPhoto>;
  /** Rejects with PhotoUploadCancelledError once `signal` is aborted. */
  upload(
    tankId: string,
    data: PhotoInboxUpload,
    onProgress: (fraction: number) => void,
    signal: AbortSignal
  ): Promise<PhotoInboxItem>;
  /** Adds an inbox photo to the tank's shots (target 'tank', no crop). */
  assign(tankId: string, id: number): Promise<PhotoInboxAssignResult>;
  /** Called after a batch uploaded something to the tank's inbox (or left photos there). */
  onTankChanged(tankId: string): void;
  /** Called now and then while photos arrive, to update the tank's inbox count. */
  refreshCount(tankId: string): void;
  /** Called now and then while a tank batch adds photos, and when it ends: Tank Shots and the tanks list. */
  refreshTankShots(tankId: string): void;
}

interface Listenable {
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

interface WakeLockHandle {
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

/** The browser APIs the queue uses, injectable for tests. */
export interface UploadQueueEnvironment {
  document: Listenable & { readonly hidden: boolean };
  window: Listenable;
  /** navigator.wakeLock, when the browser has it. */
  wakeLock?: { request(type: 'screen'): Promise<WakeLockHandle> } | null;
  /** localStorage, or null when it can't be used. May throw. */
  storage(): Storage | null;
  /** Pause while the page is hidden: on touch devices, which suspend hidden pages. */
  pauseWhenHidden: boolean;
}

/** True for failures a Retry can fix (and a tank batch's assigns that may still work). */
export function isRetryable(outcome: UploadOutcome | null): boolean {
  if (outcome?.kind === 'left_in_inbox') return outcome.retryable === true;
  return (
    outcome?.kind === 'failed' &&
    (outcome.reason === 'network' || outcome.reason === 'server' || outcome.reason === 'unreadable')
  );
}

interface FileJob {
  id: string;
  file: File;
  hash: string | null;
  checked: boolean;
  stage: UploadFileStage;
  outcome: UploadOutcome | null;
  /** Fraction of the current upload sent. */
  progress: number;
  /** An attempt got no answer or a 5xx, so the server may have this photo from it. */
  sentBefore: boolean;
  /** A tank batch's photo in the inbox, still to be assigned: its id, and status when known. */
  inboxId: number | null;
  inboxStatus: PhotoInboxStatus | null;
  /** Its assign's retries so far, and when it may be sent (parked off the upload slots until then). */
  assign: AssignState | null;
}

interface AssignState {
  notBefore: number;
  retries: number;
  busyAnswers: number;
  inProgressWaits: number;
  processingWaits: number;
}

/**
 * The tank is gone (deleted, or not this user's). The backend has no code for
 * it, only the message: 404 "Tank not found" from the route or the assign.
 */
function isTankNotFound(error: unknown): boolean {
  return (
    error instanceof PhotoInboxError &&
    error.status === 404 &&
    /tank not found/i.test(error.message)
  );
}

interface BatchJob {
  id: string;
  tankId: string;
  tankName: string;
  assignTo: UploadAssignTo | null;
  files: FileJob[];
  status: UploadBatchStatus;
  /** Stopped because the tank is gone: its photos left in the inbox can't be added. */
  tankGone: boolean;
  stage: UploadBatchStage | null;
  stopMessage: string | null;
  resolveDone: ((result: BatchResult) => void) | null;
}

function failure(reason: UploadFailureReason, message: string): UploadOutcome {
  return { kind: 'failed', reason, message };
}

/** Worth retrying: the request got no answer, or the server failed (5xx). */
function isTransient(error: unknown): boolean {
  return (
    error instanceof PhotoInboxNetworkError ||
    (error instanceof PhotoInboxError && error.status >= 500)
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function countsOf(files: FileJob[]): UploadCounts {
  const counts: UploadCounts = {
    uploaded: 0,
    inInbox: 0,
    onLivestock: 0,
    failed: 0,
    skipped: 0,
    assigned: 0,
    leftInInbox: 0,
  };
  for (const { outcome } of files) {
    if (outcome?.kind === 'uploaded') counts.uploaded++;
    else if (outcome?.kind === 'assigned') counts.assigned++;
    else if (outcome?.kind === 'left_in_inbox') counts.leftInInbox++;
    else if (outcome?.kind === 'in_inbox') counts.inInbox++;
    else if (outcome?.kind === 'on_livestock') counts.onLivestock++;
    else if (outcome?.kind === 'failed') counts.failed++;
    else if (outcome?.kind === 'skipped') counts.skipped++;
  }
  return counts;
}

function viewOf(batch: BatchJob): UploadBatchView {
  let settled = 0;
  let hashed = 0;
  let sent = 0;
  for (const job of batch.files) {
    if (job.outcome || job.hash) hashed++;
    if (job.outcome) settled++;
    else if (job.stage === 'uploading') sent += job.progress;
    // Uploaded, waiting for its assign: nearly done.
    else if (job.inboxId !== null) sent += 0.9;
  }
  const total = batch.files.length;
  return {
    id: batch.id,
    tankId: batch.tankId,
    tankName: batch.tankName,
    ...(batch.assignTo && { assignTo: batch.assignTo }),
    status: batch.status,
    stage: batch.stage,
    total,
    hashed,
    settled,
    counts: countsOf(batch.files),
    progress: total ? Math.min(1, (settled + sent) / total) : 1,
    files: batch.files.map((job) => ({
      id: job.id,
      name: job.file.name,
      stage: job.stage,
      outcome: job.outcome,
    })),
    stopMessage: batch.stopMessage,
  };
}

/** Message shown when the server says the batch can't go on (no session, no such tank). */
function stopMessageFor(error: PhotoInboxError): string {
  if (error.status === 401) {
    return 'Your session expired. Sign in again and pick the same photos; ones already uploaded are skipped.';
  }
  return error.message;
}

function positiveCount(value: unknown): number {
  const count = Number(value);
  return Number.isInteger(count) && count > 0 ? count : 0;
}

/**
 * The "waiting in Photos to sort" marker: `{"<tankId>": count}`, or a bare
 * count from before the tank was stored (no tank, so no Sort now).
 */
function parseUnsortedMarker(value: string | null): InterruptedToSort[] {
  if (value === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return [];
  }
  if (typeof parsed === 'number') {
    const count = positiveCount(parsed);
    return count ? [{ tankId: null, count }] : [];
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return [];
  return Object.entries(parsed)
    .map(([tankId, count]) => ({ tankId, count: positiveCount(count) }))
    .filter((entry) => entry.count > 0);
}

export function createPhotoUploadQueue(
  deps: UploadQueueDeps,
  env: UploadQueueEnvironment
): PhotoUploadQueue {
  const batches: BatchJob[] = [];
  const listeners = new Set<() => void>();
  let snapshot: UploadQueueSnapshot = {
    batches: [],
    pause: null,
    interrupted: 0,
    interruptedToSort: [],
  };
  let batchCount = 0;
  let interrupted = 0;
  let interruptedToSort: InterruptedToSort[] = [];
  let restored = false;

  let active = false;
  let running = false;
  /** Bumped by clear(), so work started before it stops touching the queue. */
  let generation = 0;
  /** Aborted (and replaced) by clear(): stops the uploads in flight. */
  let uploads = new AbortController();
  /** A 429 or 503 busy: nothing is sent until `until`. */
  let hold: { kind: 'rate_limited' | 'busy'; until: number } | null = null;
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let lastProgressAt = -Infinity;
  let progressTimer: ReturnType<typeof setTimeout> | null = null;
  let wakeLock: WakeLockHandle | null = null;
  let requestingWakeLock = false;
  /** The values last written to the markers; null means none written (or removed). */
  const markerValues: Record<string, string | null> = {
    [UNFINISHED_MARKER_KEY]: null,
    [UNSORTED_MARKER_KEY]: null,
  };

  // -- waiting for conditions ------------------------------------------------

  let waiters: { ready: () => boolean; resolve: () => void }[] = [];

  /** Resolves once `ready()` holds; re-checked on every state change. */
  function waitUntil(ready: () => boolean): Promise<void> {
    if (ready()) return Promise.resolve();
    return new Promise((resolve) => waiters.push({ ready, resolve }));
  }

  function wake(): void {
    const waiting = waiters;
    waiters = [];
    for (const waiter of waiting) {
      if (waiter.ready()) waiter.resolve();
      else waiters.push(waiter);
    }
  }

  function hiddenPause(): boolean {
    return env.pauseWhenHidden && env.document.hidden;
  }

  function isPaused(): boolean {
    return hold !== null || hiddenPause();
  }

  /** Resolves when the queue may send, or the batch was stopped (or cleared). */
  const whenRunnable = (batch: BatchJob): Promise<void> =>
    waitUntil(() => batch.status === 'stopped' || !isPaused());

  // -- publishing ------------------------------------------------------------

  function unfinishedCount(): number {
    let count = 0;
    for (const batch of batches) for (const job of batch.files) if (!job.outcome) count++;
    return count;
  }

  /**
   * Photos a reload would lose: unfinished ones, and failures Retry could fix
   * in batches still shown. A tank batch's photo already in the inbox isn't
   * lost: it is counted by unsortedCount.
   */
  function notUploadedCount(): number {
    let count = 0;
    for (const batch of batches) {
      for (const job of batch.files) {
        if (job.outcome?.kind === 'left_in_inbox') continue;
        if ((!job.outcome && job.inboxId === null) || isRetryable(job.outcome)) count++;
      }
    }
    return count;
  }

  /** A tank batch's photos a reload would leave in the inbox: waiting for their assign, or left there. */
  function unsortedByTank(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const batch of batches) {
      for (const job of batch.files) {
        if ((!job.outcome && job.inboxId !== null) || job.outcome?.kind === 'left_in_inbox')
          counts[batch.tankId] = (counts[batch.tankId] ?? 0) + 1;
      }
    }
    return counts;
  }

  function currentPause(): UploadPause {
    if (hold) return { kind: hold.kind, resumeAt: hold.until };
    if (active && hiddenPause()) return { kind: 'hidden' };
    return null;
  }

  function publish(): void {
    syncActivity();
    snapshot = {
      batches: batches.map(viewOf),
      pause: currentPause(),
      interrupted,
      interruptedToSort,
    };
    listeners.forEach((listener) => listener());
    wake();
  }

  /** Publishes upload progress, at most every PROGRESS_INTERVAL_MS (the last value at the end of the wait). */
  function publishProgress(): void {
    if (progressTimer !== null) return;
    const wait = lastProgressAt + PROGRESS_INTERVAL_MS - Date.now();
    if (wait <= 0) {
      lastProgressAt = Date.now();
      publish();
      return;
    }
    progressTimer = setTimeout(() => {
      progressTimer = null;
      lastProgressAt = Date.now();
      publish();
    }, wait);
  }

  // -- while photos remain: wake lock, leave warning, marker -----------------

  const onVisibilityChange = (): void => {
    if (!env.document.hidden) void acquireWakeLock();
    publish();
  };

  const onBeforeUnload = (event: Event): void => {
    event.preventDefault();
    // Older browsers show the prompt only when returnValue is set.
    (event as BeforeUnloadEvent).returnValue = true;
  };

  /** Waiting more than a minute to send anything: no reason to keep the screen on. */
  function longHold(): boolean {
    return hold !== null && hold.until - Date.now() > WAKE_LOCK_MAX_HOLD_MS;
  }

  async function acquireWakeLock(): Promise<void> {
    if (!env.wakeLock || wakeLock || requestingWakeLock || !active || env.document.hidden) return;
    if (longHold()) return;
    requestingWakeLock = true;
    try {
      const sentinel = await env.wakeLock.request('screen');
      if (!active || longHold()) {
        void sentinel.release().catch(() => undefined);
        return;
      }
      wakeLock = sentinel;
      // The browser releases the lock when the page is hidden; it is asked for again when visible.
      sentinel.addEventListener('release', () => {
        if (wakeLock === sentinel) wakeLock = null;
      });
    } catch {
      // Refused (battery saver, permissions policy): upload without it.
    } finally {
      requestingWakeLock = false;
    }
  }

  function releaseWakeLock(): void {
    const sentinel = wakeLock;
    wakeLock = null;
    if (sentinel) void sentinel.release().catch(() => undefined);
  }

  /** Writes the marker's value; null removes it. */
  function writeMarker(key: string, value: string | null, force = false): void {
    if (value === markerValues[key] && !force) return;
    markerValues[key] = value;
    try {
      const storage = env.storage();
      if (value !== null) storage?.setItem(key, value);
      else storage?.removeItem(key);
    } catch {
      // Storage blocked or full: the marker is a convenience.
    }
  }

  function syncActivity(): void {
    const unfinished = unfinishedCount();
    if (unfinished > 0 && !active) {
      active = true;
      env.document.addEventListener('visibilitychange', onVisibilityChange);
      env.window.addEventListener('beforeunload', onBeforeUnload);
      void acquireWakeLock();
    } else if (unfinished === 0 && active) {
      active = false;
      env.document.removeEventListener('visibilitychange', onVisibilityChange);
      env.window.removeEventListener('beforeunload', onBeforeUnload);
      releaseWakeLock();
    }
    const notUploaded = notUploadedCount();
    writeMarker(UNFINISHED_MARKER_KEY, notUploaded > 0 ? String(notUploaded) : null);
    const unsorted = unsortedByTank();
    writeMarker(
      UNSORTED_MARKER_KEY,
      Object.keys(unsorted).length ? JSON.stringify(unsorted) : null
    );
  }

  // -- holding the queue on 429 and 503 busy --------------------------------

  /**
   * Sends nothing for `seconds` (at least 1); a longer hold already in place is
   * kept. During a hold of more than a minute the screen may sleep: the wake
   * lock is released, and asked for again when the hold ends.
   */
  function holdQueue(kind: 'rate_limited' | 'busy', seconds: number): void {
    const until = Date.now() + Math.max(1, seconds) * 1000;
    if (hold && hold.until >= until) return;
    hold = { kind, until };
    if (holdTimer) clearTimeout(holdTimer);
    holdTimer = setTimeout(() => {
      holdTimer = null;
      hold = null;
      void acquireWakeLock();
      publish();
    }, until - Date.now());
    if (longHold()) releaseWakeLock();
    publish();
  }

  function endHold(): void {
    if (holdTimer) clearTimeout(holdTimer);
    holdTimer = null;
    hold = null;
  }

  // -- the work ----------------------------------------------------------------

  function settle(job: FileJob, outcome: UploadOutcome): void {
    job.outcome = outcome;
    job.stage = 'done';
    job.progress = 0;
    publish();
  }

  /**
   * The photo is in the tank's inbox. An inbox batch settles it with
   * `outcome`; a tank batch keeps it for its assign (status 'uploading' while
   * the server still processes it), or leaves it to sort when /check gave no
   * id to assign it by.
   */
  function reachedInbox(
    batch: BatchJob,
    job: FileJob,
    outcome: Extract<UploadOutcome, { kind: 'uploaded' | 'in_inbox' }>
  ): void {
    if (batch.assignTo !== 'tank') {
      settle(job, outcome);
      return;
    }
    if (outcome.kind === 'in_inbox') tookOverWaitingPhoto(batch.tankId);
    if (outcome.id === null) {
      settle(job, { kind: 'left_in_inbox', id: null, reason: 'Already in Photos to sort' });
      return;
    }
    job.inboxId = outcome.id;
    job.inboxStatus =
      outcome.kind === 'uploaded' ? (outcome.processing ? 'uploading' : 'pending') : outcome.status;
    job.stage = 'waiting';
    job.progress = 0;
    publish();
  }

  /**
   * A tank batch found one of its photos already in the tank's inbox (/check,
   * or a 409 duplicate): lowers that tank's "waiting in Photos to sort" count
   * by one, as this batch now adds the photo or reports it left to sort. The
   * marker keeps only counts, not hashes, so any photo found there counts:
   * picking the same photos again clears the message, picking different ones
   * keeps it. A message without a tank (old marker) is left to dismiss.
   */
  function tookOverWaitingPhoto(tankId: string): void {
    interruptedToSort = interruptedToSort
      .map((entry) => (entry.tankId === tankId ? { ...entry, count: entry.count - 1 } : entry))
      .filter((entry) => entry.count > 0);
  }

  function stop(batch: BatchJob, message: string): void {
    batch.status = 'stopped';
    batch.stopMessage = message;
    publish();
  }

  async function hashFiles(batch: BatchJob): Promise<void> {
    batch.stage = 'hashing';
    publish();
    const seen = new Set<string>();
    for (const job of batch.files)
      if (job.hash && job.outcome?.kind !== 'skipped') seen.add(job.hash);

    for (const job of batch.files) {
      if (job.outcome || job.hash) continue;
      await whenRunnable(batch);
      if (batch.status === 'stopped') return;
      job.stage = 'hashing';
      publish();
      let hash: string;
      try {
        hash = await deps.hash(job.file);
      } catch {
        settle(job, failure('unreadable', "Couldn't read this photo"));
        continue;
      }
      job.hash = hash;
      if (seen.has(hash)) {
        settle(job, { kind: 'skipped', why: 'duplicate' });
      } else {
        seen.add(hash);
        job.stage = 'waiting';
        publish();
      }
    }
  }

  /** Runs a /check, waiting out 429s and retrying transient failures; null when it can't be done. */
  async function checkWithRetries(
    batch: BatchJob,
    hashes: string[]
  ): Promise<PhotoInboxCheckResponse | null> {
    for (let attempt = 0; ; ) {
      await whenRunnable(batch);
      if (batch.status === 'stopped') return null;
      try {
        return await deps.check(batch.tankId, hashes);
      } catch (error) {
        if (error instanceof RateLimitError) {
          holdQueue('rate_limited', error.retryAfterSeconds ?? DEFAULT_RATE_LIMIT_WAIT_S);
          continue;
        }
        const delay = RETRY_DELAYS_MS[attempt];
        if (isTransient(error) && delay !== undefined) {
          attempt++;
          await sleep(delay);
          continue;
        }
        return null;
      }
    }
  }

  async function checkFiles(batch: BatchJob): Promise<void> {
    const pending = batch.files.filter((job) => !job.outcome && job.hash && !job.checked);
    // One photo: let the upload say it's a duplicate, with its id.
    if (batch.files.length === 1 || pending.length === 0) return;
    batch.stage = 'checking';
    publish();

    for (let i = 0; i < pending.length; i += PHOTO_INBOX_CHECK_MAX) {
      const chunk = pending.slice(i, i + PHOTO_INBOX_CHECK_MAX);
      // When the check can't be done the photos are uploaded anyway; a duplicate is then a 409.
      const found = await checkWithRetries(
        batch,
        chunk.map((job) => job.hash as string)
      );
      if (batch.status === 'stopped') return;
      const inInbox = new Set(found?.in_inbox ?? []);
      const inInboxItems = new Map((found?.in_inbox_items ?? []).map((row) => [row.hash, row]));
      const onLivestock = new Map((found?.on_livestock ?? []).map((match) => [match.hash, match]));
      for (const job of chunk) {
        job.checked = true;
        const match = onLivestock.get(job.hash as string);
        if (match !== undefined) {
          settle(job, {
            kind: 'on_livestock',
            livestockName: match.livestock_name,
            ...(match.target === 'tank' && { target: 'tank' as const }),
          });
        } else if (inInbox.has(job.hash as string)) {
          // A server older than tank shots sends no in_inbox_items: no id, no status.
          const row = inInboxItems.get(job.hash as string);
          reachedInbox(batch, job, {
            kind: 'in_inbox',
            id: row?.id ?? null,
            status: row?.status ?? null,
          });
        }
      }
      publish();
    }
  }

  function uploadFields(job: FileJob, prepared: PreparedPhoto): PhotoInboxUpload {
    return {
      photo: prepared.blob,
      filename: prepared.filename,
      content_hash: job.hash as string,
      taken_at_local: prepared.capture?.local ?? null,
      taken_at_offset: prepared.capture?.offset ?? null,
      file_last_modified: prepared.lastModified,
    };
  }

  /**
   * Uploads one prepared photo with its retries; true when it was uploaded. A
   * 429, or a 503 busy (the server's image processing is full), holds the
   * whole queue for Retry-After, so the other upload slot doesn't send a whole
   * photo just to be turned away too. Neither uses up the two retries for
   * network errors and 5xx.
   */
  async function uploadOne(
    batch: BatchJob,
    job: FileJob,
    prepared: PreparedPhoto
  ): Promise<boolean> {
    let busyAnswers = 0;
    let ownDuplicateWaits = 0;
    for (let attempt = 0; ; ) {
      await whenRunnable(batch);
      if (batch.status === 'stopped') return false; // marked skipped with the rest
      job.stage = 'uploading';
      job.progress = 0;
      publish();
      try {
        const item = await deps.upload(
          batch.tankId,
          uploadFields(job, prepared),
          (fraction) => {
            job.progress = fraction;
            publishProgress();
          },
          uploads.signal
        );
        reachedInbox(batch, job, { kind: 'uploaded', id: item.id });
        return true;
      } catch (error) {
        if (error instanceof PhotoUploadCancelledError) return false; // cleared
        job.progress = 0;
        if (error instanceof RateLimitError) {
          job.stage = 'waiting';
          holdQueue('rate_limited', error.retryAfterSeconds ?? DEFAULT_RATE_LIMIT_WAIT_S);
          continue;
        }
        if (error instanceof PhotoInboxBusyError) {
          if (++busyAnswers > MAX_BUSY_RETRIES) {
            settle(job, failure('server', 'The server is busy. Try again in a few minutes.'));
            return false;
          }
          job.stage = 'waiting';
          holdQueue('busy', error.retryAfterSeconds ?? DEFAULT_BUSY_WAIT_S);
          continue;
        }
        if (error instanceof PhotoInboxConflictError) {
          const { conflict } = error;
          if (conflict.code === 'duplicate' && job.sentBefore) {
            // Probably this photo's own earlier attempt. While the server is still
            // processing it, wait and send again: if processing fails, the server
            // takes the photo again instead of answering 409.
            const wait =
              conflict.status === 'uploading'
                ? OWN_DUPLICATE_WAITS_MS[ownDuplicateWaits]
                : undefined;
            if (wait !== undefined) {
              ownDuplicateWaits++;
              job.stage = 'retrying';
              publish();
              await sleep(wait);
              continue;
            }
            reachedInbox(batch, job, {
              kind: 'uploaded',
              id: conflict.id,
              ...(conflict.status === 'uploading' && { processing: true as const }),
            });
            return true;
          }
          if (conflict.code === 'duplicate') {
            reachedInbox(batch, job, {
              kind: 'in_inbox',
              id: conflict.id,
              status: conflict.status,
            });
          } else if (conflict.code === 'on_livestock') {
            settle(job, {
              kind: 'on_livestock',
              livestockName: conflict.livestock_name,
              ...(conflict.target === 'tank' && { target: 'tank' as const }),
            });
          } else if (conflict.code === 'inbox_full') stop(batch, conflict.message);
          else settle(job, failure('server', conflict.message));
          return false;
        }
        if (error instanceof PhotoTooLargeError) {
          settle(job, failure('too_large', 'Too large'));
          return false;
        }
        if (error instanceof PhotoRejectedError) {
          settle(job, failure('unsupported', error.message));
          return false;
        }
        if (error instanceof PhotoInboxError && [401, 403, 404].includes(error.status)) {
          if (isTankNotFound(error)) batch.tankGone = true;
          stop(batch, stopMessageFor(error));
          return false;
        }
        if (isTransient(error)) job.sentBefore = true;
        const delay = RETRY_DELAYS_MS[attempt];
        if (isTransient(error) && delay !== undefined) {
          attempt++;
          job.stage = 'retrying';
          publish();
          await sleep(delay);
          continue;
        }
        settle(
          job,
          error instanceof PhotoInboxNetworkError
            ? failure('network', 'Network error')
            : failure('server', error instanceof PhotoInboxError ? error.message : 'Upload failed')
        );
        return false;
      }
    }
  }

  /** The assign's state, made when it is first needed: a photo still processing waits first. */
  function assignStateOf(job: FileJob): AssignState {
    if (!job.assign) {
      job.assign = {
        notBefore: 0,
        retries: 0,
        busyAnswers: 0,
        inProgressWaits: 0,
        processingWaits: 0,
      };
      if (job.inboxStatus === 'uploading') parkForProcessing(job);
    }
    return job.assign;
  }

  /** Not before `ms` from now; the slot is free meanwhile, and a publish when it is due wakes it. */
  function park(job: FileJob, ms: number): void {
    const state = job.assign as AssignState;
    state.notBefore = Date.now() + ms;
    job.stage = 'retrying';
    setTimeout(publish, ms);
    publish();
  }

  /** A photo still being processed: try after the next wait; false when the waits are used up. */
  function parkForProcessing(job: FileJob): boolean {
    const state = job.assign as AssignState;
    const wait = ASSIGN_PROCESSING_WAITS_MS[state.processingWaits];
    if (wait === undefined) return false;
    state.processingWaits++;
    park(job, wait);
    return true;
  }

  /**
   * Sends one assign of a tank batch's inbox photo to Tank Shots (target
   * 'tank', no crop). 'assigned' when it was added; 'settled' when it got
   * another outcome; 'again' when it is to be sent again (parked until
   * `assign.notBefore`, or held with the whole queue).
   *
   * Assigning again is idempotent (an assigned photo comes back as it is), so
   * network errors, 5xx (502 "the assign didn't finish") and 409 in_progress
   * are tried again after a wait; 429 and busy hold the queue as for uploads.
   * A photo the server still processes answers 404 until it is pending, so it
   * is tried again after ASSIGN_PROCESSING_WAITS_MS. Waits are spent off the
   * upload slots. When it can't be added, the photo stays in the inbox with
   * the reason.
   */
  async function assignOnce(
    batch: BatchJob,
    job: FileJob
  ): Promise<'assigned' | 'settled' | 'again'> {
    const id = job.inboxId as number;
    const state = assignStateOf(job);
    const started = generation;
    const gone = (): boolean => started !== generation || batch.status === 'stopped';
    const leave = (reason: string, retryable: boolean): 'settled' => {
      settle(job, {
        kind: 'left_in_inbox',
        id,
        reason,
        ...(retryable && { retryable: true as const }),
      });
      return 'settled';
    };

    await whenRunnable(batch);
    // A stopped batch leaves it in the inbox (runBatch); after clear() nothing matters.
    if (gone()) return 'settled';
    job.stage = 'assigning';
    publish();
    try {
      await deps.assign(batch.tankId, id);
      if (started !== generation) return 'settled';
      settle(job, { kind: 'assigned', id });
      return 'assigned';
    } catch (error) {
      if (gone()) return 'settled';
      if (error instanceof RateLimitError) {
        job.stage = 'waiting';
        holdQueue('rate_limited', error.retryAfterSeconds ?? DEFAULT_RATE_LIMIT_WAIT_S);
        return 'again';
      }
      if (error instanceof PhotoInboxBusyError) {
        if (++state.busyAnswers > MAX_BUSY_RETRIES) {
          return leave('The server is busy. Try again in a few minutes.', true);
        }
        job.stage = 'waiting';
        holdQueue('busy', error.retryAfterSeconds ?? DEFAULT_BUSY_WAIT_S);
        return 'again';
      }
      if (error instanceof PhotoInboxConflictError) {
        const { conflict } = error;
        if (conflict.code === 'in_progress') {
          const wait = ASSIGN_IN_PROGRESS_WAITS_MS[state.inProgressWaits];
          if (wait === undefined) {
            return leave('It was being sorted at the same time. Check Photos to sort.', true);
          }
          state.inProgressWaits++;
          park(job, wait);
          return 'again';
        }
        if (conflict.code === 'assigned_elsewhere') {
          // Sorted to an organism meanwhile: a photo is in one place.
          if (conflict.target === 'tank') {
            settle(job, { kind: 'assigned', id });
            return 'assigned';
          }
          settle(job, {
            kind: 'on_livestock',
            livestockName: conflict.livestock_name ?? 'an organism',
            target: 'organism',
          });
          return 'settled';
        }
        return leave(conflict.message, false);
      }
      if (isTankNotFound(error)) {
        batch.tankGone = true;
        stop(batch, (error as Error).message);
        return 'settled';
      }
      if (error instanceof PhotoInboxError && error.status === 404) {
        // Not pending: still being processed, when the server last said so.
        if (job.inboxStatus === 'uploading') {
          if (parkForProcessing(job)) return 'again';
          return leave('Still being processed. Try again in a minute.', true);
        }
        settle(job, failure('server', "This photo isn't in Photos to sort any more"));
        return 'settled';
      }
      if (error instanceof PhotoInboxError && [401, 403].includes(error.status)) {
        stop(batch, stopMessageFor(error));
        return 'settled';
      }
      if (isTransient(error)) {
        const delay = ASSIGN_RETRY_DELAYS_MS[state.retries];
        if (delay !== undefined) {
          state.retries++;
          park(job, delay);
          return 'again';
        }
        return leave(
          error instanceof PhotoInboxNetworkError ? 'Network error' : (error as Error).message,
          true
        );
      }
      return leave(
        error instanceof PhotoInboxError ? error.message : "Couldn't add it to Tank Shots",
        false
      );
    }
  }

  /** Calls `refresh` every COUNT_REFRESH_UPLOADS calls, or on a call COUNT_REFRESH_MS after the last refresh. */
  function every(refresh: () => void): () => void {
    let since = 0;
    let refreshedAt = Date.now();
    return () => {
      since++;
      if (since < COUNT_REFRESH_UPLOADS && Date.now() - refreshedAt < COUNT_REFRESH_MS) return;
      since = 0;
      refreshedAt = Date.now();
      try {
        refresh();
      } catch {
        // Only what is on screen.
      }
    };
  }

  /**
   * Prepares and uploads the batch's remaining hashed photos, and in a tank
   * batch assigns each one in the inbox, in the same slot. uploaded: any was
   * uploaded; assigned: any was added to Tank Shots. One retried after this
   * run hashed is left for the next run.
   */
  async function uploadFiles(batch: BatchJob): Promise<{ uploaded: boolean; assigned: boolean }> {
    const queue = batch.files.filter((job) => !job.outcome && job.hash);
    const result = { uploaded: false, assigned: false };
    if (queue.length === 0 || batch.status === 'stopped') return result;
    batch.stage = 'uploading';
    publish();

    // In the inbox already (/check, or an earlier run): only the assign is left.
    const toAssign = queue.filter((job) => job.inboxId !== null);
    const toUpload = queue.filter((job) => job.inboxId === null);
    const ready: { job: FileJob; prepared: PreparedPhoto }[] = [];
    let preparing = true;
    const stopped = (): boolean => batch.status === 'stopped';

    // The inbox count (Livestock page, nav badge) follows along every few
    // uploads; a tank batch's photos leave the inbox again, so Tank Shots
    // follows instead.
    const refreshCount = every(() => deps.refreshCount(batch.tankId));
    const refreshShots = every(() => deps.refreshTankShots(batch.tankId));
    const uploaded = (): void => {
      result.uploaded = true;
      if (batch.assignTo !== 'tank') refreshCount();
    };
    // Photos waiting to be assigned; one parked for a retry is taken once it is due.
    for (const job of toAssign) assignStateOf(job);
    const isDue = (job: FileJob): boolean => (job.assign?.notBefore ?? 0) <= Date.now();
    const takeDue = (): FileJob | undefined => {
      const index = toAssign.findIndex(isDue);
      return index < 0 ? undefined : toAssign.splice(index, 1)[0];
    };
    const assign = async (job: FileJob): Promise<void> => {
      const answer = await assignOnce(batch, job);
      if (answer === 'again') toAssign.push(job);
      else if (answer === 'assigned') {
        result.assigned = true;
        refreshShots();
      }
    };

    const prepareAll = async (): Promise<void> => {
      for (const job of toUpload) {
        await waitUntil(() => stopped() || (ready.length < PREPARE_AHEAD && !isPaused()));
        if (stopped()) break;
        job.stage = 'preparing';
        publish();
        try {
          const prepared = await deps.prepare(job.file);
          job.stage = 'waiting';
          ready.push({ job, prepared });
          publish();
        } catch (error) {
          settle(
            job,
            error instanceof PhotoPrepareError
              ? failure(error.reason, error.message)
              : failure('unreadable', "Couldn't read this photo")
          );
        }
      }
      preparing = false;
      publish();
    };

    const uploadAll = async (): Promise<void> => {
      for (;;) {
        await waitUntil(
          () =>
            stopped() ||
            toAssign.some(isDue) ||
            ready.length > 0 ||
            (!preparing && toAssign.length === 0)
        );
        if (stopped()) return;
        const assignNext = takeDue();
        if (assignNext) {
          await assign(assignNext);
          continue;
        }
        const next = ready.shift();
        if (!next) {
          // The other slot took it; wait for the next one unless everything is done.
          if (preparing || toAssign.length > 0) continue;
          return;
        }
        publish(); // room to prepare the next one
        if (await uploadOne(batch, next.job, next.prepared)) uploaded();
        // In the inbox now: its assign is next in this slot (or parked while it processes).
        if (!next.job.outcome && next.job.inboxId !== null) {
          assignStateOf(next.job);
          toAssign.push(next.job);
        }
      }
    };

    await Promise.all([
      prepareAll(),
      ...Array.from({ length: UPLOAD_CONCURRENCY }, () => uploadAll()),
    ]);
    return result;
  }

  async function runBatch(batch: BatchJob): Promise<void> {
    const started = generation;
    batch.status = 'running';
    batch.stopMessage = null;
    batch.tankGone = false;
    let done = { uploaded: false, assigned: false };
    try {
      await hashFiles(batch);
      await checkFiles(batch);
      done = await uploadFiles(batch);
    } catch {
      // Not expected: everything above handles its own errors. Never leave photos hanging.
      for (const job of batch.files)
        if (!job.outcome) settle(job, failure('server', 'Upload failed'));
    }

    // Cast: TypeScript keeps the 'running' assigned above across the awaits.
    if ((batch.status as UploadBatchStatus) === 'stopped') {
      for (const job of batch.files) {
        if (!job.outcome) {
          // A tank batch's photo in the inbox stays there; Retry adds it.
          job.outcome =
            job.inboxId !== null
              ? {
                  kind: 'left_in_inbox',
                  id: job.inboxId,
                  reason: batch.tankGone
                    ? 'Not added: the tank was not found'
                    : 'Not added: the upload stopped',
                  ...(!batch.tankGone && { retryable: true as const }),
                }
              : { kind: 'skipped', why: 'stopped' };
          job.stage = 'done';
        }
      }
    } else {
      // A photo retried while the batch ran is still to do: run the batch again.
      batch.status = batch.files.some((job) => !job.outcome) ? 'waiting' : 'finished';
    }
    batch.stage = null;
    publish();
    if (started !== generation) return; // cleared meanwhile: nobody is waiting for it

    // A tank batch refreshes the inbox only when it left photos there (no nav badge flicker).
    const inboxChanged =
      batch.assignTo === 'tank'
        ? batch.files.some(
            (job) => job.outcome?.kind === 'left_in_inbox' && job.outcome.id !== null
          )
        : done.uploaded;
    try {
      if (inboxChanged) deps.onTankChanged(batch.tankId);
      if (done.assigned) deps.refreshTankShots(batch.tankId);
    } catch {
      // A cache refresh failing doesn't change what was uploaded.
    }
    if (batch.resolveDone && batch.status !== 'waiting') {
      batch.resolveDone({
        batchId: batch.id,
        files: batch.files.map((job) => ({
          name: job.file.name,
          outcome: job.outcome as UploadOutcome,
        })),
      });
      batch.resolveDone = null;
    }
  }

  function kick(): void {
    if (running) return;
    running = true;
    const started = generation;
    void (async (): Promise<void> => {
      try {
        for (;;) {
          const next = batches.find((batch) => batch.files.some((job) => !job.outcome));
          if (!next) break;
          await runBatch(next);
          if (started !== generation) return; // cleared: a new loop runs what's queued now
        }
      } finally {
        if (started === generation) {
          running = false;
          publish();
        }
      }
    })();
  }

  function findBatch(batchId: string): BatchJob | undefined {
    return batches.find((batch) => batch.id === batchId);
  }

  function requeue(batch: BatchJob, jobs: FileJob[]): void {
    if (jobs.length === 0) return;
    for (const job of jobs) {
      // A failed photo is sent again, checked afresh when an assign found it
      // gone from the inbox; one left in the inbox is assigned again.
      if (job.outcome?.kind === 'failed') {
        if (job.inboxId !== null) {
          job.checked = false;
          job.sentBefore = false;
        }
        job.inboxId = null;
        job.inboxStatus = null;
      }
      job.assign = null;
      job.outcome = null;
      job.stage = 'waiting';
      job.progress = 0;
    }
    if (batch.status !== 'running') {
      batch.status = 'waiting';
      batch.stopMessage = null;
    }
    publish();
    kick();
  }

  return {
    subscribe(listener): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot(): UploadQueueSnapshot {
      return snapshot;
    },

    enqueue({ tankId, tankName, files, assignTo }): EnqueuedBatch | null {
      if (files.length === 0) return null;
      const id = `batch-${++batchCount}`;
      let resolveDone: (result: BatchResult) => void = () => undefined;
      const done = new Promise<BatchResult>((resolve) => {
        resolveDone = resolve;
      });
      batches.push({
        id,
        tankId,
        tankName,
        assignTo: assignTo ?? null,
        files: files.map((file, index) => ({
          id: `${id}-${index}`,
          file,
          hash: null,
          checked: false,
          stage: 'waiting',
          outcome: null,
          progress: 0,
          sentBefore: false,
          inboxId: null,
          inboxStatus: null,
          assign: null,
        })),
        status: 'waiting',
        tankGone: false,
        stage: null,
        stopMessage: null,
        resolveDone,
      });
      // Picking the photos again is what the "didn't upload" message asked for.
      // The "waiting in Photos to sort" one is kept: the user may have picked
      // other photos. It goes down as this batch finds photos in the inbox
      // (reachedInbox).
      interrupted = 0;
      restored = true;
      publish();
      kick();
      return { batchId: id, done };
    },

    retry(batchId, fileId): void {
      const batch = findBatch(batchId);
      const job = batch?.files.find((file) => file.id === fileId);
      if (batch && job && isRetryable(job.outcome)) requeue(batch, [job]);
    },

    retryFailed(batchId): void {
      const batch = findBatch(batchId);
      if (batch)
        requeue(
          batch,
          batch.files.filter((job) => isRetryable(job.outcome))
        );
    },

    dismiss(batchId): void {
      const index = batches.findIndex((batch) => batch.id === batchId);
      const batch = batches[index];
      if (!batch || batch.files.some((job) => !job.outcome)) return;
      batches.splice(index, 1);
      publish();
    },

    restoreInterrupted(): void {
      if (restored) return;
      restored = true;
      const read = (key: string): string | null => {
        try {
          const storage = env.storage();
          const value = storage?.getItem(key) ?? null;
          storage?.removeItem(key);
          return value;
        } catch {
          return null; // Storage unavailable: nothing to restore.
        }
      };
      interrupted = positiveCount(read(UNFINISHED_MARKER_KEY));
      interruptedToSort = parseUnsortedMarker(read(UNSORTED_MARKER_KEY));
      publish();
    },

    dismissInterrupted(): void {
      interrupted = 0;
      publish();
    },

    dismissInterruptedToSort(tankId): void {
      interruptedToSort = interruptedToSort.filter((entry) => entry.tankId !== tankId);
      publish();
    },

    clear(): void {
      // An assign already sent can't be aborted (it isn't an upload): its
      // answer is ignored, and the photo is added or stays in the inbox.
      generation++;
      running = false;
      uploads.abort();
      uploads = new AbortController();
      for (const batch of batches) {
        batch.status = 'stopped';
        batch.resolveDone = null;
      }
      batches.length = 0;
      endHold();
      if (progressTimer) clearTimeout(progressTimer);
      progressTimer = null;
      interrupted = 0;
      interruptedToSort = [];
      restored = true; // a marker read now would be this user's own
      writeMarker(UNFINISHED_MARKER_KEY, null, true);
      writeMarker(UNSORTED_MARKER_KEY, null, true);
      publish();
    },
  };
}

function browserEnvironment(): UploadQueueEnvironment {
  return {
    document,
    window,
    wakeLock:
      typeof navigator !== 'undefined' && 'wakeLock' in navigator ? navigator.wakeLock : null,
    storage: () => window.localStorage,
    pauseWhenHidden:
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches,
  };
}

/** The app's queue. */
export const photoUploadQueue = createPhotoUploadQueue(
  {
    hash: computeContentHash,
    check: (tankId, hashes) => photoInboxApi.check(tankId, hashes),
    prepare: prepareForUpload,
    upload: (tankId, data, onProgress, signal) =>
      photoInboxApi.upload(tankId, data, onProgress, signal),
    assign: (tankId, id) => photoInboxApi.assign(tankId, id, { target: 'tank' }),
    // The grid's first page only: invalidating it would fetch every page it has loaded.
    onTankChanged: (tankId) => refreshInboxAfterUploads(queryClient, tankId),
    refreshCount: (tankId) => {
      void queryClient.invalidateQueries({ queryKey: photoInboxKeys.count(tankId) });
    },
    refreshTankShots: (tankId) => refreshTankShots(queryClient, tankId),
  },
  browserEnvironment()
);

/** The app queue's current state, re-rendering on every change. */
export function usePhotoUploadQueue(): UploadQueueSnapshot {
  return useSyncExternalStore(photoUploadQueue.subscribe, photoUploadQueue.getSnapshot);
}
